/* eslint-disable @typescript-eslint/no-namespace */
import { DateTime } from '@mode-switch/express';
import { Encoding, EncField } from '@mode-vegas/vegas';
import { SchemaModel } from './schema-model';
import { CompiledFormula, FormulaOperations } from '@mode/shared/util-switch';
import * as ChartEncoding from './chart-encoding';

export namespace ChartHierarchy {
  export const DatePartHierarchy = [
    DateTime.Unit.Year,
    DateTime.Unit.Quarter,
    DateTime.Unit.Month,
    DateTime.Unit.Weekday,
    DateTime.Unit.Day,
    DateTime.Unit.Hour,
    DateTime.Unit.Minute,
    DateTime.Unit.Second,
  ];

  export const DateTruncHierarchy = [
    DateTime.Unit.Year,
    DateTime.Unit.Quarter,
    DateTime.Unit.Month,
    DateTime.Unit.Week,
    DateTime.Unit.Day,
    DateTime.Unit.Hour,
    DateTime.Unit.Minute,
    DateTime.Unit.Second,
  ];

  export interface DrillLevel {
    field: EncField;
    currentDatePart: DateTime.Unit;
    nextDatePart: DateTime.Unit;
    hasDateTrunc: boolean;
  }

  export function checkLowestDatePart(model: SchemaModel, encoding: Encoding): boolean {
    let compiled: CompiledFormula;
    let isLowestDatePart = false;
    let dateField = encoding.x[0];

    if (!dateField) {
      if (encoding.marks.series.length) {
        dateField = encoding.marks.series[0].properties.color.values[0];
      } else if (encoding.column.length) {
        dateField = encoding.column[0];
      } else {
        dateField = encoding.marks.properties.color.values[0];
      }
    }

    if (dateField) {
      compiled = model.compileEncField(dateField);

      if (compiled.hasDateFunc) {
        const datePart = compiled.dateFuncUnit;

        if (datePart === makeNextDatePart(model, encoding, dateField)) {
          isLowestDatePart = true;
        }
      }
    }

    return isLowestDatePart;
  }

  export function nextDatePart(model: SchemaModel, encoding: Encoding): string | undefined {
    switch (ChartEncoding.shape(encoding)) {
      case ChartEncoding.Shape.Grouped:
        return nextGroupedDatePart(model, encoding);
      case ChartEncoding.Shape.Combo:
      case ChartEncoding.Shape.Stacked:
        return nextStackedDatePart(model, encoding);
      case ChartEncoding.Shape.Radial:
        return nextRadialDatePart(model, encoding);
    }
  }

  export function nextDateFormula(model: SchemaModel, encField: EncField): string | undefined {
    const field = model.compileEncField(encField);
    if (field.isValid && field.hasDateFunc && field.dateFuncUnit) {
      if (field.hasDateTrunc) {
        return FormulaOperations.dateTrunc(field, getNextDatePart(field.dateFuncUnit, true, true));
      } else {
        return FormulaOperations.datePart(field, getNextDatePart(field.dateFuncUnit, false, true));
      }
    }

    return undefined;
  }

  export function getNextDatePart(current: DateTime.Unit, trunc: boolean, forward: boolean): DateTime.Unit {
    const hierarchy = trunc ? DateTruncHierarchy : DatePartHierarchy;

    const index = hierarchy.indexOf(current);
    let next = forward ? index + 1 : index - 1;
    if (next < 0) {
      next = 0;
    }

    if (next >= hierarchy.length) {
      next = hierarchy.length - 1;
    }

    return hierarchy[next];
  }

  export function getDrillLevelsForEncoding(
    model: SchemaModel,
    encoding: Encoding,
    seriesId?: string
  ): Array<DrillLevel> {
    const drillLevels: Array<DrillLevel> = [];
    // Gather up the drillable channel fields based on the encoding.
    // Note: In review session with Ying and Nascif on 15 Feb 2022,
    // it was decided that we would not allow drilling on the text
    // or size channels. In the future, we may allow multiple marks
    // in those channels and we don't know how that will impact drilling.
    let channelFields = [];
    if (encoding.marks.channel === '*') {
      channelFields = [
        ...encoding.x,
        ...encoding.y,
        ...encoding.column,
        ...encoding.row,
        ...encoding.marks.properties.color.values,
        ...encoding.marks.properties.detail.values,
        ...encoding.marks.properties.angle.values,
      ];
    } else {
      channelFields =
        encoding.marks.channel === 'x'
          ? [...encoding.y, ...encoding.row, ...encoding.column]
          : [...encoding.x, ...encoding.row, ...encoding.column];
      if (seriesId != null) {
        const targetSeries = encoding.marks.series.find((series) => series.field === seriesId);
        if (targetSeries) {
          channelFields = channelFields.concat([
            ...targetSeries.properties.angle.values,
            ...targetSeries.properties.color.values,
            ...targetSeries.properties.detail.values,
          ]);
        }
      }
    }
    // Include any date fields from the available channels in the drill levels
    channelFields.forEach((encField) => {
      const field = model.compileEncField(encField);
      if (field.isValid && field.hasDateFunc && field.dateFuncUnit) {
        const nextLevel = getNextDatePart(field.dateFuncUnit, field.hasDateTrunc, true);
        drillLevels.push({
          field: encField,
          currentDatePart: field.dateFuncUnit,
          nextDatePart: nextLevel,
          hasDateTrunc: field.hasDateTrunc,
        });
      }
    });
    return drillLevels;
  }

  function nextStackedDatePart(model: SchemaModel, encoding: Encoding): string | undefined {
    return makeNextDatePart(model, encoding, encoding.x[0]);
  }

  function nextGroupedDatePart(model: SchemaModel, encoding: Encoding): string | undefined {
    return makeNextDatePart(model, encoding, encoding.column[0]);
  }

  function nextRadialDatePart(model: SchemaModel, encoding: Encoding): string | undefined {
    const { series, properties } = encoding.marks;
    const props = series.length ? series[0].properties : properties;
    return makeNextDatePart(model, encoding, props.color.values[0]);
  }

  function makeNextDatePart(model: SchemaModel, encoding: Encoding, encField: EncField): DateTime.Unit | undefined {
    const field = model.compileEncField(encField);
    if (field.isValid && field.hasDateFunc && field.dateFuncUnit) {
      return getNextDatePart(field.dateFuncUnit, field.hasDateTrunc, true);
    }

    return undefined;
  }
}
